/**
 * One row of the settings dialog, drawn from its registry definition.
 *
 * There is no per-setting markup anywhere in this app: a definition in
 * `@proxmon/shared` says what kind of value it is and this decides what that
 * looks like. Adding a setting is one entry in that table, and the form grows
 * a field for it.
 *
 * Drafts are held as text, not as typed values. A number field whose value is
 * parsed on every keystroke cannot be emptied to retype it, and a
 * comma-separated list that is split and rejoined on every keystroke eats the
 * comma you just typed. Everything is coerced once, on save, by the same
 * function the server validates with.
 */

import type { SettingDef } from '@proxmon/shared';

export type Draft = string | boolean;

export interface SettingsFieldProps {
  def: SettingDef;
  value: Draft;
  /** the stored secret is non-empty; we are never told what it is */
  secretSet: boolean;
  /** the operator has typed into this secret, so it will be sent on save */
  secretTouched: boolean;
  disabled: boolean;
  onChange(value: Draft): void;
}

export function SettingsField({
  def,
  value,
  secretSet,
  secretTouched,
  disabled,
  onChange,
}: SettingsFieldProps) {
  const id = `setting-${def.key}`;

  const badges = def.restart ? <span className="setting-badge">restart</span> : null;

  // A checkbox reads better with its label beside it than above it, so the
  // boolean case gets the whole row rather than the shared layout.
  if (def.kind === 'boolean') {
    return (
      <div className="setting-row field-row">
        <div className="text">
          <strong>
            <label htmlFor={id} className="inline-label">
              <input
                id={id}
                type="checkbox"
                checked={value === true}
                disabled={disabled}
                onChange={(event) => onChange(event.target.checked)}
              />
              {def.label}
            </label>
            {badges}
          </strong>
          <span>{def.help}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="setting-row field-row">
      <div className="text">
        <strong>
          <label htmlFor={id}>{def.label}</label>
          {badges}
          {def.kind === 'secret' && (
            <span className={secretSet || secretTouched ? 'setting-badge ok' : 'setting-badge'}>
              {secretTouched ? 'will be saved' : secretSet ? 'configured' : 'not set'}
            </span>
          )}
        </strong>
        <span>{def.help}</span>
        <div className="setting-input">
          {def.kind === 'enum' ? (
            <select id={id} value={String(value)} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
              {def.options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : (
            <input
              id={id}
              type={def.kind === 'secret' ? 'password' : def.kind === 'number' ? 'number' : 'text'}
              value={String(value)}
              disabled={disabled}
              spellCheck={false}
              autoComplete={def.kind === 'secret' ? 'new-password' : 'off'}
              min={def.min}
              max={def.max}
              placeholder={
                def.kind === 'secret' && secretSet ? 'unchanged — type to replace' : (def.placeholder ?? '')
              }
              onChange={(event) => onChange(event.target.value)}
            />
          )}
          {def.unit && <span className="setting-unit">{def.unit}</span>}
          {def.kind === 'secret' && (secretSet || secretTouched) && (
            <button
              type="button"
              className="btn ghost small"
              disabled={disabled}
              title="Clear the stored value"
              onClick={() => onChange('')}
            >
              Clear
            </button>
          )}
        </div>
        {def.kind === 'number' && (def.min !== undefined || def.max !== undefined) && (
          <span className="setting-range">
            {def.min ?? '—'} to {def.max ?? '—'}
            {def.unit ? ` ${def.unit}` : ''}; anything outside is clamped
          </span>
        )}
      </div>
    </div>
  );
}
